import { ElMessage } from 'element-plus'
import { clearLocalStorageKeeping } from './tool'
import { getToken, getWordToken } from './token'
import { encryptRequestData } from './request-crypt'

/** 单词服务（斩单词）独立域名，鉴权使用 WordAppToken */
export const WORD_BASEURL =
  ((import.meta.env as any).VITE_WORD_BASEURL as string) || 'https://voc-ielts.ytaxx.com'
const WORD_DOMAIN = WORD_BASEURL.replace(/^https?:\/\//, '')
const isWordUrl = (url: string) => url.includes(WORD_DOMAIN)

const generateUUID = () => {
  const key = 'alpaca_uuid'
  let uuid = localStorage.getItem(key)
  if (!uuid) {
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
      .replace(/[xy]/g, (c) => {
        const r = (Math.random() * 36) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(36)
      })
      .substring(0, 24)
    localStorage.setItem(key, uuid)
  }
  return uuid
}

/** 单词服务使用与主站不同的请求头 */
const wordHeaders = () => ({
  clientType: 'pcweb',
  serviceVersion: '18',
  clientVersion: '1.0.0',
  uuid: generateUUID(),
  appVersion: '1.0.0',
  sourceBazaar: 'pcweb',
  deviceType: 'pcweb'
})

/** 单词服务请求优先使用 WordAppToken，无则回退主站 token */
const getRequestToken = (url: string) =>
  isWordUrl(url) ? getWordToken() || getToken() : getToken()

// 定义基础类型
type Scalar = string | number | boolean | null | undefined
type Params = Record<string, Scalar>

export interface ApiResponse<T = any> {
  code: number
  data: T
  msg: string
}

export interface RequestConfig extends RequestInit {
  skipMsg?: boolean // 是否跳过错误提示
  params?: Params // GET 请求参数
}

// 基础 Header 配置
export const baseHeaders = {
  clientType: 'pcweb',
  Serviceversion: '42',
  deviceType: 'pcweb'
}

// 需要处理 Token 失效的状态码
const TOKEN_INVALID_CODES = [10006, 100017, 100016, 100018]

// 防止重复触发 Token 失效逻辑的锁
let isRelogging = false

/**
 * 构建查询字符串
 */
const buildQueryString = (url: string, params?: Params): string => {
  if (!params) return url
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value))
    }
  })
  const queryString = searchParams.toString()
  return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url
}

/**
 * 统一处理响应数据
 */
const handleResponse = async <T>(
  response: Response,
  config: RequestConfig
): Promise<ApiResponse<T>> => {
  // 1. 处理 HTTP 状态码错误 (404, 500 等)
  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status} ${response.statusText}`)
  }

  // 2. 解析 JSON
  let resData: ApiResponse<T>
  try {
    resData = await response.json()
  } catch (e) {
    throw new Error('响应数据格式错误' + e)
  }

  // 非标准数据返回时不做code判断

  // 3. 业务逻辑判断
  const isSuccess = resData.code === 0 || !resData.code
  if (!isSuccess) {
    // 3.1 处理 Token 失效（单词服务域名不触发主站登出）
    if (TOKEN_INVALID_CODES.includes(resData.code) && !isWordUrl(response.url)) {
      if (!isRelogging) {
        isRelogging = true
        if (localStorage.getItem('token')) {
          clearLocalStorageKeeping()
          ElMessage.error({ message: '登录已过期，请重新登录', duration: 2000 })
          setTimeout(() => {
            isRelogging = false // 重置锁（虽然reload了，但如果是SPA路由跳转需要重置）
            location.reload()
          }, 1500)
        }
      }
      // Token 失效也算作业务错误，reject 出去
      return Promise.reject(resData)
    }

    // 3.2 处理普通业务错误
    if (!config.skipMsg) {
      ElMessage.error({
        message: resData.msg || '请求失败',
        duration: 3000,
        offset: 120
      })
    }

    // 重点：这里 reject 会被 http 的 catch 捕获
    return Promise.reject(resData)
  }

  return resData
}

/**
 * 统一处理网络/系统错误
 * 修复点：区分“业务逻辑错误”和“纯网络错误”
 */
const handleError = (error: any, skipMsg = false) => {
  // 【关键修复】
  // 如果 error 包含 code，说明它是 handleResponse 抛出的业务错误
  // 已经在 handleResponse 里弹过窗了，这里直接透传，不再 ElMessage
  if (error && error.code) {
    return Promise.reject(error)
  }
  // 由调用页面展示重试时，网络错误和取消请求也不重复弹窗。
  if (skipMsg) return Promise.reject(error)

  // 处理原生 Error (如网络断开、超时、解析失败)
  const msg = error.message || '网络请求失败'

  // 避免 "TypeError: Failed to fetch" 这种对用户不友好的提示，可以转译一下
  const showMsg = msg.includes('fetch') ? '网络连接异常' : msg

  ElMessage.error({ message: showMsg, duration: 3000 })
  return Promise.reject(error)
}

/**
 * 核心请求函数
 */
const http = async <T = any>(url: string, config: RequestConfig = {}): Promise<ApiResponse<T>> => {
  // 1. 合并 Headers
  const token = getRequestToken(url)

  // 【修复点】变量名统一，避免 fetch 用错变量
  const headers = new Headers({
    ...(isWordUrl(url) ? wordHeaders() : baseHeaders),
    ...config.headers,
    'Content-Type': 'application/json'
  })

  if (token) {
    headers.set('token', token)
  }

  // 2. 处理 URL 参数
  const finalUrl = buildQueryString(url, config.params)

  // 3. 发起请求
  try {
    // 移除自定义属性，只保留原生 fetch 配置
    const { ...fetchConfig } = config

    const response = await fetch(finalUrl, {
      ...fetchConfig,
      headers // 这里现在使用的是上面定义的 headers 实例
    })

    return await handleResponse<T>(response, config)
  } catch (error) {
    // 无论是 handleResponse 的 reject，还是 fetch 的网络 fail，都会走到这里
    return handleError(error, config.skipMsg)
  }
}

// 导出对象
const request = {
  get: <T = any>(url: string, params?: Params, config: Omit<RequestConfig, 'params'> = {}) => {
    return http<T>(url, { ...config, method: 'GET', params })
  },

  post: <T = any>(url: string, data?: object, config: RequestConfig = {}) => {
    // 登录/验证码类接口的手机号、密码类接口的各密码字段在发送前按白名单加密，
    // 调用方计算 sign 时仍是明文（明文手机号）
    const body = JSON.stringify(encryptRequestData(url, data))
    return http<T>(url, { ...config, method: 'POST', body })
  },

  put: <T = any>(url: string, data?: object, config: RequestConfig = {}) => {
    return http<T>(url, { ...config, method: 'PUT', body: JSON.stringify(data) })
  },

  upload: <T = any>(url: string, data: FormData | any, config: RequestConfig = {}) => {
    const uploadConfig = { ...config, method: 'POST', body: data }
    // 上传文件时，必须删除 Content-Type，让浏览器自动生成 boundary
    if (uploadConfig.headers) {
      // @ts-ignore
      delete uploadConfig.headers['Content-Type']
    } else {
      // 如果没有传入 headers，我们需要创建一个机制去移除 http 函数里默认加的 'application/json'
      // 这里的处理方式稍微 trick 一点，我们在 http 函数里加判断，或者在这里覆盖 headers
      // 更好的方式是在 http 内部判断，但为了复用 http，我们可以在这里传入一个特殊的 header 标记
    }

    // 针对 upload 的特殊处理：
    // 由于 http 函数内部强制加了 'application/json'，我们需要在这里重写 http 调用逻辑
    // 或者修改 http 函数。鉴于 upload 场景较少，我们可以稍微修改一下 http 内部逻辑：
    // (在上面 http 函数中，Headers 构造时如果不想要默认 json，需要外部传参控制，
    // 这里为了简单，建议直接在这里手动构建 header 传进去覆盖)

    return request.customHttp<T>(url, uploadConfig)
  },

  // 增加一个不带默认 json header 的底层调用，用于 upload
  customHttp: async <T = any>(url: string, config: RequestConfig = {}): Promise<ApiResponse<T>> => {
    const token = getRequestToken(url)
    const headers = new Headers({
      ...(isWordUrl(url) ? wordHeaders() : baseHeaders),
      ...config.headers
      // 注意：这里不默认加 Content-Type
    })
    if (token) headers.set('token', token)

    try {
      const { params, ...fetchConfig } = config
      const response = await fetch(buildQueryString(url, params), { ...fetchConfig, headers })
      return await handleResponse<T>(response, config)
    } catch (error) {
      return handleError(error)
    }
  },

  postForm: <T = any>(url: string, data?: Record<string, any>, config: RequestConfig = {}) => {
    const body = data
      ? Object.keys(data)
          .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
          .join('&')
      : ''
    return request.customHttp<T>(url, {
      ...config,
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...config.headers },
      body
    })
  },

  assemble: buildQueryString
}

export default request
